import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { questionLabel } from "@/lib/questionOrdinals";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ShieldCheck, Plus, X, ExternalLink, Loader2, CheckCircle2, AlertCircle, XCircle, PauseCircle, Download, FileText, MessageSquare, ChevronDown, ChevronRight, ShieldAlert, Calendar, Upload, ClipboardCheck, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { exportActionPlanCsv, exportActionPlanPdf } from "@/reports/actionPlan/exportActionPlan";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// Tabs removed — CAP IA now uses flat sections instead of sub-tabs for respondents.
import { FindingThread } from "@/components/cap/FindingThread";
import { FindingHistory } from "@/components/cap/FindingHistory";
import { UnifiedEvidence } from "@/components/cap/UnifiedEvidence";
import { FindingContextBlock } from "@/components/cap/FindingContextBlock";
import { RemediationGuidance } from "@/components/cap/RemediationGuidance";
import { ReviewerAssessmentPanel } from "@/components/cap/FindingContextBlock";
import { uploadEvidenceFile } from "@/api/uploadEvidence";

import { AssessmentCommunicationBubble } from "@/components/cap/AssessmentCommunicationBubble";

interface EvidenceLink {
  id: string;
  link_url: string;
  created_at: string;
  uploaded_by_user_id: string;
}

interface ActionPlanFinding {
  id: string;
  display_id?: string | null;
  display_q_number?: number | null;
  display_seq?: number | null;
  question_id: number;
  control_code: string | null;
  finding_title: string | null;
  risk_level: string | null;
  priority_level: string | null;
  timeframe: string | null;
  recommended_action: string | null;
  recommended_measures: string | null;
  remediation_path: string | null;
  owner: string | null;
  due_date: string | null;
  cap_status: string | null;
  respondent_notes: string | null;
  reviewer_comment: string | null;
  validation_status: string | null;
  validated_at: string | null;
  csv_domain: string | null;
  nist_function: string | null;
  example_evidence: string[] | null;
  source_type?: string | null;
  source_key?: string | null;
  justification?: string | null;
  observed_gap?: string | null;
  recommended_additional_evidence?: string | null;
  evidence_reviewed?: string | null;
  owners?: string[] | null;
  evidence_links: EvidenceLink[];
}

import { normalizeEvidenceUrl } from "@/lib/capExampleEvidence";
import { usePortfolioServiceModels } from "@/hooks/useAssessmentServiceModels";

interface ActionPlanProgress {
  total_items: number;
  completed_items: number;
  in_progress_items: number;
  submitted_items: number;
  open_items: number;
  deferred_items: number;
}

interface ActionPlanPayload {
  assessment_id: string;
  version: number;
  published_at: string;
  published_by: string;
  findings: ActionPlanFinding[];
  progress?: ActionPlanProgress;
}

type HorizonKey = "overdue" | "due_soon" | "upcoming" | "later";

const HORIZON_META: Record<HorizonKey, { label: string; window: string; tone: "danger" | "warning" | "neutral" }> = {
  overdue: { label: "Overdue", window: "Past due date", tone: "danger" },
  due_soon: { label: "Due Soon", window: "Next 30 days", tone: "warning" },
  upcoming: { label: "Upcoming", window: "31–90 days", tone: "neutral" },
  later: { label: "Later / No Due Date", window: "Beyond 90 days or unscheduled", tone: "neutral" },
};

function resolveDueBucket(dueDate: string | null): HorizonKey {
  if (!dueDate) return "later";
  const due = new Date(dueDate);
  if (isNaN(due.getTime())) return "later";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueMidnight = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diffDays = Math.floor((dueMidnight.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "overdue";
  if (diffDays <= 30) return "due_soon";
  if (diffDays <= 90) return "upcoming";
  return "later";
}

function dueSortValue(dueDate: string | null): number {
  if (!dueDate) return Number.POSITIVE_INFINITY;
  const t = new Date(dueDate).getTime();
  return isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

const PRIORITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  immediate: 1,
  moderate: 2,
  medium: 2,
  "quick win": 2,
  low: 3,
  strategic: 3,
};

function priorityRank(p: string | null): number {
  return PRIORITY_RANK[(p ?? "").toLowerCase().trim()] ?? 4;
}

const RISK_COLORS: Record<string, string> = {
  Critical: "bg-red-700 text-white",
  HIGH: "bg-destructive text-destructive-foreground",
  High: "bg-destructive text-destructive-foreground",
  MODERATE: "bg-yellow-500/90 text-white",
  Moderate: "bg-yellow-500/90 text-white",
  LOW: "bg-green-600 text-white",
  Low: "bg-green-600 text-white",
};

import { CAP_STATUS_OPTIONS, CAP_STATUS_META, normalizeCapStatus } from "@/components/cap/capStatus";
const STATUS_OPTIONS = CAP_STATUS_OPTIONS;

export default function ActionPlanView() {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const readOnly = searchParams.get("mode") === "pm";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<ActionPlanPayload | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const { models: serviceModels } = usePortfolioServiceModels(assessmentId);

  const load = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const rpcName = readOnly ? "pm_get_action_plan" : "respondent_get_action_plan";
      const { data, error } = await (supabase.rpc as any)(
        rpcName,
        { p_assessment_id: assessmentId }
      );
      if (error) {
        setError(error.message ?? "Could not load Action Plan");
        setPlan(null);
      } else {
        setPlan(data as ActionPlanPayload);
        setError(null);
        const { data: activeAssessments } = await (supabase.rpc as any)("get_my_active_assessment");
        const active = Array.isArray(activeAssessments)
          ? activeAssessments.find((a: any) => a.id === assessmentId)
          : null;
        setOrgName(active?.org_name ?? null);
      }
    } catch (e: any) {
      setError(e?.message ?? "Could not load Action Plan");
    } finally {
      setLoading(false);
    }
  }, [assessmentId, readOnly]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen bg-background p-6 md:p-10">
        <div className="max-w-3xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Card className="border-destructive/20 bg-destructive/5">
            <CardContent className="p-6">
              <p className="text-sm text-destructive">
                {error ?? "Action Plan is not available."}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const publishedAt = new Date(plan.published_at).toLocaleString();

  return (
    <div className="min-h-screen bg-background p-6 md:p-10">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={!plan.findings.length}>
                  <Download className="h-4 w-4 mr-1.5" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportActionPlanCsv(plan)}>
                  <FileText className="h-4 w-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportActionPlanPdf(plan)}>
                  <FileText className="h-4 w-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Badge variant="outline" className="text-xs">
              Action Plan
            </Badge>
            {readOnly && (
              <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800 border">
                Read Only · Portfolio Manager
              </Badge>
            )}
          </div>
        </div>

        <Card className="overflow-hidden border-2 shadow-md">
          <div className="h-1.5 w-full bg-gradient-to-r from-primary via-primary/70 to-accent" />
          <CardContent className="p-6 bg-gradient-to-br from-primary/10 via-primary/[0.03] to-transparent">
            <div className="flex items-start gap-4">
              <div className="rounded-xl bg-primary/15 border border-primary/30 p-3 shrink-0">
                <ShieldCheck className="h-7 w-7 text-primary" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl font-semibold tracking-tight">
                  Corrective Action Plan
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                  {readOnly
                    ? "Read-only view of the latest Corrective Action Plan progress for this assessment."
                    : "Update status, add progress notes, and attach evidence links as you complete remediation activities."}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="secondary" className="text-[11px] font-normal">
                    Version <span className="ml-1 font-semibold">{plan.version}</span>
                  </Badge>
                  <Badge variant="secondary" className="text-[11px] font-normal">
                    Published <span className="ml-1 font-semibold">{publishedAt}</span>
                  </Badge>
                  <Badge variant="secondary" className="text-[11px] font-normal">
                    <span className="font-semibold mr-1">{plan.findings.length}</span>
                    action item{plan.findings.length === 1 ? "" : "s"}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>


        {plan.findings.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No findings have been recorded for this Action Plan.
            </CardContent>
          </Card>
        ) : (
          <>
            <ProgressSection progress={plan.progress} totalFallback={plan.findings.length} />
            <GroupedFindings findings={plan.findings} assessmentId={plan.assessment_id} onChanged={load} readOnly={readOnly} serviceModels={serviceModels} />
          </>
        )}
      </div>
      {!readOnly && assessmentId && (
        <AssessmentCommunicationBubble assessmentId={assessmentId} orgName={orgName} />
      )}
    </div>
  );
}

interface ActionItemCardProps {
  finding: ActionPlanFinding;
  assessmentId: string;
  onChanged: () => void;
  readOnly?: boolean;
  serviceModels?: string[];
}

function ActionItemCard({ finding, assessmentId, onChanged, readOnly = false, serviceModels = [], defaultExpanded = false }: ActionItemCardProps & { defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [status, setStatus] = useState(finding.cap_status ?? "open");
  const [notes, setNotes] = useState(finding.respondent_notes ?? "");
  const [newLink, setNewLink] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [addingLink, setAddingLink] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [evidenceReload, setEvidenceReload] = useState(0);


  useEffect(() => {
    setStatus(finding.cap_status ?? "open");
    setNotes(finding.respondent_notes ?? "");
  }, [finding.cap_status, finding.respondent_notes]);

  const updateItem = async (next: { status?: string; notes?: string }) => {
    const { error } = await (supabase.rpc as any)("respondent_update_action_item", {
      p_finding_id: finding.id,
      p_status: next.status ?? null,
      p_notes: next.notes ?? null,
    });
    if (error) {
      toast.error(error.message ?? "Failed to save");
      return false;
    }
    return true;
  };

  const handleStatusChange = async (val: string) => {
    setStatus(val);
    setSavingStatus(true);
    const ok = await updateItem({ status: val });
    setSavingStatus(false);
    if (ok) {
      toast.success("Status updated");
      onChanged();
    }
  };

  const handleNotesBlur = async () => {
    if (notes === (finding.respondent_notes ?? "")) return;
    setSavingNotes(true);
    const ok = await updateItem({ notes });
    setSavingNotes(false);
    if (ok) toast.success("Notes saved");
  };

  const handleAddLink = async () => {
    const url = newLink.trim();
    if (!url) return;
    setAddingLink(true);
    const { error } = await (supabase.rpc as any)("respondent_add_action_evidence_link", {
      p_finding_id: finding.id,
      p_link_url: url,
    });
    setAddingLink(false);
    if (error) {
      toast.error(error.message ?? "Failed to add link");
      return;
    }
    setNewLink("");
    toast.success("Evidence link added");
    setEvidenceReload((v) => v + 1);
    onChanged();
  };

  const handleRemoveLink = async (id: string) => {
    const { error } = await (supabase.rpc as any)("respondent_remove_action_evidence_link", {
      p_evidence_id: id,
    });
    if (error) {
      toast.error(error.message ?? "Failed to remove link");
      return;
    }
    toast.success("Evidence link removed");
    setEvidenceReload((v) => v + 1);
    onChanged();

  };

  const handleUploadFiles = async (files: FileList | null) => {
    if (!files?.length || readOnly) return;
    setUploadingFiles(true);
    try {
      for (const file of Array.from(files)) {
        await uploadEvidenceFile({
          assessmentId,
          questionId: finding.question_id,
          findingId: finding.id,
          file,
        });
      }
      toast.success(files.length === 1 ? "Evidence file uploaded" : `${files.length} evidence files uploaded`);
      setEvidenceReload((v) => v + 1);
      onChanged();

    } catch (e: any) {
      toast.error(e?.message ?? "Failed to upload evidence file");
    } finally {
      setUploadingFiles(false);
    }
  };

  const statusMeta = CAP_STATUS_META[normalizeCapStatus(status)];
  const dueLabel = finding.due_date ? new Date(finding.due_date).toLocaleDateString() : null;
  const validation = finding.validation_status ? VALIDATION_META[finding.validation_status] : null;

  const riskBorder =
    finding.risk_level === "Critical" || finding.risk_level === "HIGH" || finding.risk_level === "High"
      ? "border-l-destructive"
      : finding.risk_level === "MODERATE" || finding.risk_level === "Moderate"
      ? "border-l-yellow-500"
      : finding.risk_level === "LOW" || finding.risk_level === "Low"
      ? "border-l-emerald-500"
      : "border-l-primary/60";

  return (
    <Card className={`border-2 border-l-[6px] ${riskBorder} shadow-md hover:shadow-lg transition-shadow ${expanded ? "ring-1 ring-primary/20" : ""}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-muted/40 transition-colors bg-gradient-to-r from-muted/30 to-transparent"
        aria-expanded={expanded}
      >

        <div className="mt-1 shrink-0">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {finding.display_id && (
                  <span
                    className="shrink-0 rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary"
                    title="Finding tracking reference (persistent within this assessment)"
                  >
                    {finding.display_id}
                  </span>
                )}
                {finding.display_q_number != null && (
                  <span
                    className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold text-muted-foreground"
                    title="Frozen source question number for this assessment"
                  >
                    {questionLabel(finding.display_q_number)}
                  </span>
                )}
                <h4 className="text-sm font-semibold leading-snug truncate">
                  {finding.finding_title || <span className="italic text-muted-foreground">Untitled finding</span>}
                </h4>
              </div>
              {finding.control_code && (
                <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{finding.control_code}</div>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {finding.risk_level && (
                <Badge className={`text-[10px] gap-1 ${RISK_COLORS[finding.risk_level] ?? "bg-muted text-foreground"}`}>
                  <ShieldAlert className="h-2.5 w-2.5" /> {finding.risk_level}
                </Badge>
              )}
              {finding.priority_level && (
                <Badge variant="outline" className="text-[10px]">{finding.priority_level}</Badge>
              )}
              <Badge variant="outline" className="text-[10px]">{statusMeta.label}</Badge>
              {validation && (
                <Badge variant="outline" className={`text-[10px] ${validation.iconClass}`}>
                  {validation.label}
                </Badge>
              )}
              {dueLabel && (
                <Badge variant="outline" className="text-[10px] gap-1">
                  <Calendar className="h-2.5 w-2.5" /> {dueLabel}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </button>
      {expanded && (
      <CardContent className="p-5 pt-0 space-y-4 border-t bg-muted/10">
        <div className="pt-4" />

        {/* Reviewer validation feedback (read-only) */}
        {finding.validation_status && (
          <ReviewerValidationBanner
            status={finding.validation_status}
            comment={finding.reviewer_comment}
            validatedAt={finding.validated_at}
          />
        )}

        {/* Finding Context — read-only summary (Reviewer Assessment moved below) */}
        <FindingContextBlock finding={finding} variant="respondent" hideReviewerAssessment />

        {/* Step 1 — Understand what needs to be done */}
        <StepSection
          step={1}
          accent="blue"
          icon={ShieldCheck}
          title="Remediation Guidance"
          subtitle="What to do — required actions, recommended measures, and remediation path."
          collapsible
        >
          <RemediationGuidance finding={finding} serviceModels={serviceModels} hideHeader />

        </StepSection>

        {/* Step 2 — Understand why the finding was raised */}
        <StepSection
          step={2}
          accent="amber"
          icon={FileText}
          title="Reviewer Assessment"
          subtitle="Why this finding was raised — observed gap, rationale, and evidence reviewed."
          collapsible
          defaultCollapsed
        >
          <ReviewerAssessmentPanel finding={finding} hideHeader />
        </StepSection>

        {/* Step 3 — Upload proof */}
        <StepSection
          step={3}
          accent="emerald"
          icon={ExternalLink}
          title="Upload Supporting Evidence"
          subtitle="Attach the documents or links that demonstrate remediation. Conversation is for discussion; evidence is for proof."
          collapsible
          defaultCollapsed
        >

          <div className="space-y-3">
            {/* Respondent-managed evidence links */}
            <div className="rounded-md border-2 border-dashed border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2">
              <div className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
                <ExternalLink className="h-3.5 w-3.5 text-emerald-600" />
                Evidence Links ({finding.evidence_links.length})
              </div>
              {finding.evidence_links.length > 0 && (
                <ul className="space-y-1.5">
                  {finding.evidence_links.map((link) => {
                    const href = normalizeEvidenceUrl(link.link_url) ?? "#";
                    return (
                      <li
                        key={link.id}
                        className="flex items-center justify-between gap-2 text-xs bg-background rounded px-2.5 py-1.5 border"
                      >
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-primary hover:underline truncate min-w-0"
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          <span className="truncate">{link.link_url}</span>
                        </a>
                        {!readOnly && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            onClick={() => handleRemoveLink(link.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {!readOnly && (
                <div className="flex gap-2">
                  <Input
                    value={newLink}
                    onChange={(e) => setNewLink(e.target.value)}
                    placeholder="Paste evidence URL (https://…)"
                    className="h-9 text-sm bg-background"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddLink();
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={handleAddLink}
                    disabled={addingLink || !newLink.trim()}
                    className="shrink-0"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Evidence
                  </Button>
                </div>
              )}
            </div>

            {!readOnly && (
              <div className="rounded-lg border-2 border-dashed border-emerald-500/40 bg-emerald-500/[0.04] p-4 text-center space-y-2">
                <div className="text-xs font-semibold text-foreground flex items-center justify-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-emerald-600" />
                  Evidence Files
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Upload screenshots, policies, reports or other supporting documents (max 20MB per file).
                </p>
                <input
                  id={`evidence-upload-${finding.id}`}
                  type="file"
                  multiple
                  className="sr-only"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.txt,.csv"
                  disabled={uploadingFiles}
                  onChange={(e) => {
                    handleUploadFiles(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={uploadingFiles}
                  onClick={() => document.getElementById(`evidence-upload-${finding.id}`)?.click()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {uploadingFiles ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading…
                    </>
                  ) : (
                    <>
                      <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Evidence Files
                    </>
                  )}
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  Uploaded files appear below and can be removed if you attach the wrong document.
                </p>
              </div>
            )}

            {/* Unified roll-up: Submitted + Requested categories */}
            <UnifiedEvidence
              findingId={finding.id}
              reloadKey={evidenceReload}
              canRemove={!readOnly}
              onChanged={onChanged}
            />

          </div>
        </StepSection>

        {/* Step 4 — Track progress and submit */}
        <StepSection
          step={4}
          accent="violet"
          icon={CheckCircle2}
          title="Update Progress & Submit"
          subtitle="Summarize current remediation status, then submit for the Reviewer to validate."
          collapsible
          defaultCollapsed
        >

          {readOnly ? (
            <div className="grid md:grid-cols-2 gap-3 text-xs bg-background border rounded p-3">
              <div>
                <div className="text-muted-foreground mb-0.5">Remediation Status</div>
                <div className="font-medium">
                  {STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground mb-0.5">Progress Summary</div>
                <div className="font-medium whitespace-pre-wrap">
                  {finding.respondent_notes?.trim() ? finding.respondent_notes : "—"}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-[260px_1fr] gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Remediation Workflow
                </label>
                <StatusWorkflow
                  status={status}
                  saving={savingStatus}
                  onChange={handleStatusChange}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Progress Summary {savingNotes && <span className="text-[10px]">(saving…)</span>}
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={handleNotesBlur}
                  placeholder="Summarize current remediation status, blockers, and overall progress…"
                  className="min-h-[60px] text-sm bg-background"
                />
              </div>
            </div>
          )}
        </StepSection>

        {/* Step 5 — Contact the Reviewer */}
        <StepSection
          step={5}
          accent="slate"
          icon={MessageSquare}
          title="Send Message to Reviewer"
          subtitle="Ask a clarifying question or discuss this finding with the assigned Reviewer."
          collapsible
          defaultCollapsed
        >

          <CollapsibleConversation findingId={finding.id} readOnly={readOnly} />
        </StepSection>

        {/* History */}
        {!readOnly && (
          <details className="group rounded-md border bg-background">
            <summary className="cursor-pointer list-none px-3 py-2 text-xs font-semibold flex items-center justify-between gap-2 select-none">
              <span className="flex items-center gap-2">
                <ClipboardListIcon />
                History
              </span>
              <span className="text-[10px] text-muted-foreground group-open:hidden">Show</span>
              <span className="text-[10px] text-muted-foreground hidden group-open:inline">Hide</span>
            </summary>
            <div className="px-3 pb-3 pt-1">
              <FindingHistory findingId={finding.id} />
            </div>
          </details>
        )}
      </CardContent>
      )}
    </Card>
  );
}

const STEP_ACCENTS: Record<string, { border: string; badge: string; icon: string }> = {
  blue:    { border: "border-l-blue-500",    badge: "bg-blue-500/10 text-blue-700 dark:text-blue-300 ring-blue-500/30",       icon: "text-blue-600 dark:text-blue-400" },
  amber:   { border: "border-l-amber-500",   badge: "bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-amber-500/30",    icon: "text-amber-600 dark:text-amber-400" },
  emerald: { border: "border-l-emerald-500", badge: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30", icon: "text-emerald-600 dark:text-emerald-400" },
  violet:  { border: "border-l-violet-500",  badge: "bg-violet-500/10 text-violet-700 dark:text-violet-300 ring-violet-500/30", icon: "text-violet-600 dark:text-violet-400" },
  slate:   { border: "border-l-slate-400",   badge: "bg-muted text-muted-foreground ring-border",                              icon: "text-muted-foreground" },
};

function StepSection({
  step,
  accent,
  icon: Icon,
  title,
  subtitle,
  collapsible = false,
  defaultCollapsed = false,
  children,
}: {
  step: number;
  accent: keyof typeof STEP_ACCENTS;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}) {
  const isCollapsible = collapsible !== false;
  const [open, setOpen] = useState(!defaultCollapsed);
  const a = STEP_ACCENTS[accent];
  return (
    <section className={`rounded-lg border border-l-4 ${a.border} bg-card shadow-sm overflow-hidden`}>
      <button
        type="button"
        onClick={() => isCollapsible && setOpen((v) => !v)}
        className={`w-full flex items-start gap-3 px-4 py-3 text-left ${isCollapsible ? "hover:bg-muted/40 transition-colors cursor-pointer" : "cursor-default"}`}
        aria-expanded={open}
        disabled={!isCollapsible}
      >

        <div className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ring-1 ${a.badge}`}>
          {step}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${a.icon}`} />
            <h3 className="text-sm font-semibold">{title}</h3>
            {isCollapsible && (
              <span className="ml-auto text-[10px] text-muted-foreground flex items-center gap-1">
                {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {open ? "Hide" : "Show"}
              </span>
            )}

          </div>
          {subtitle && (
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{subtitle}</p>
          )}
        </div>
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </section>
  );
}


function ClipboardListIcon() {
  return <FileText className="h-4 w-4 text-primary" />;
}

function CollapsibleConversation({ findingId, readOnly }: { findingId: string; readOnly: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 rounded-md border bg-card px-3 py-2 hover:bg-accent/40 transition-colors"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <MessageSquare className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold flex-1 text-left">Send Message to Reviewer</span>
        <span className="text-[10px] text-muted-foreground">
          {open ? "Hide conversation" : "Open conversation"}
        </span>
      </button>
      {open && (
        <div className="rounded-md border bg-background p-3 space-y-2">
          <p className="text-[11px] text-muted-foreground">
            Use this conversation to communicate with the Reviewer regarding remediation
            progress, evidence requests, and validation feedback.
          </p>
          <FindingThread
            findingId={findingId}
            canFulfill={!readOnly}
            canRequest={false}
            canPost={!readOnly}
            composerPlaceholder="Type a message to the Reviewer…"
          />

        </div>
      )}
    </section>
  );
}

function StatusWorkflow({
  status,
  saving,
  onChange,
}: {
  status: string;
  saving: boolean;
  onChange: (next: string) => void | Promise<void>;
}) {
  const norm = normalizeCapStatus(status);
  const helper = CAP_STATUS_META[norm].helper;

  const renderButtons = () => {
    if (norm === "open" || norm === "awaiting_evidence" || norm === "in_progress") {
      return (
        <>
          <Button size="sm" onClick={() => onChange("awaiting_reviewer")} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Submit for Review
          </Button>
          <Button size="sm" variant="outline" onClick={() => onChange("deferred")} disabled={saving}>
            Defer
          </Button>
        </>
      );
    }
    if (norm === "awaiting_reviewer") {
      return (
        <Badge className="bg-yellow-500/15 text-yellow-700 border border-yellow-500/30 hover:bg-yellow-500/15">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Awaiting Reviewer
        </Badge>
      );
    }
    if (norm === "deferred") {
      return (
        <Button size="sm" onClick={() => onChange("in_progress")} disabled={saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Resume Work
        </Button>
      );
    }
    if (norm === "closed") {
      return (
        <Badge className="bg-green-600/15 text-green-700 border border-green-600/30">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Closed
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">{renderButtons()}</div>
      {helper && <p className="text-[11px] text-muted-foreground">{helper}</p>}
    </div>
  );
}

const VALIDATION_META: Record<string, { label: string; icon: typeof CheckCircle2; cardClass: string; iconClass: string }> = {
  accepted: { label: "Accepted", icon: CheckCircle2, cardClass: "border-green-500/40 bg-green-500/10", iconClass: "text-green-600" },
  needs_more_evidence: { label: "Needs More Evidence", icon: AlertCircle, cardClass: "border-yellow-500/40 bg-yellow-500/10", iconClass: "text-yellow-600" },
  rejected: { label: "Rejected", icon: XCircle, cardClass: "border-destructive/40 bg-destructive/10", iconClass: "text-destructive" },
  deferred: { label: "Deferred", icon: PauseCircle, cardClass: "border-muted-foreground/30 bg-muted/40", iconClass: "text-muted-foreground" },
};

function ReviewerValidationBanner({
  status,
  comment,
  validatedAt,
}: {
  status: string;
  comment: string | null;
  validatedAt: string | null;
}) {
  const meta = VALIDATION_META[status] ?? VALIDATION_META.deferred;
  const Icon = meta.icon;
  return (
    <div className={`rounded-md border p-3 ${meta.cardClass}`}>
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.iconClass}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold">Reviewer Validation: {meta.label}</span>
            {validatedAt && (
              <span className="text-[10px] text-muted-foreground">
                {new Date(validatedAt).toLocaleString()}
              </span>
            )}
          </div>
          {comment && (
            <p className="text-xs mt-1 whitespace-pre-wrap text-foreground">{comment}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressSection({ progress, totalFallback }: { progress?: ActionPlanProgress; totalFallback: number }) {
  const total = progress?.total_items ?? totalFallback;
  const completed = progress?.completed_items ?? 0;
  const inProgress = progress?.in_progress_items ?? 0;
  const submitted = progress?.submitted_items ?? 0;
  const open = progress?.open_items ?? 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const stats = [
    { label: "Completed", value: completed, dot: "bg-emerald-500", tint: "bg-emerald-500/5 border-emerald-500/30" },
    { label: "In Progress", value: inProgress, dot: "bg-blue-500", tint: "bg-blue-500/5 border-blue-500/30" },
    { label: "Submitted for Review", value: submitted, dot: "bg-amber-500", tint: "bg-amber-500/5 border-amber-500/30" },
    { label: "Open", value: open, dot: "bg-muted-foreground", tint: "bg-muted/40 border-border" },
  ];

  return (
    <Card className="border-2 shadow-sm overflow-hidden">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="rounded-lg bg-primary/10 border border-primary/25 p-2">
              <TrendingUp className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">Remediation Progress</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {completed} / {total} action{total === 1 ? "" : "s"} completed
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-semibold tabular-nums leading-none">{pct}%</div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">Complete</div>
          </div>
        </div>
        <Progress value={pct} className="h-2.5" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {stats.map((s) => (
            <div key={s.label} className={`rounded-lg border p-2.5 ${s.tint}`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                {s.label}
              </div>
              <div className="text-lg font-semibold tabular-nums mt-0.5">{s.value}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


/**
 * Renders all action items in a single flat list, preserving the RPC's order:
 *   due_date ASC (nulls last) → priority → display_seq → created_at.
 *
 * No client-side sorting or priority grouping; Priority is shown via the
 * per-card badge.
 */
function GroupedFindings({ findings, assessmentId, onChanged, readOnly = false, serviceModels = [] }: { findings: ActionPlanFinding[]; assessmentId: string; onChanged: () => void; readOnly?: boolean; serviceModels?: string[] }) {
  if (findings.length === 0) return null;
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Sorted by due date. Items without a due date appear last. Priority is shown on each item.
      </p>
      {findings.map((f) => (
        <ActionItemCard
          key={f.id}
          finding={f}
          assessmentId={assessmentId}
          onChanged={onChanged}
          readOnly={readOnly}
          serviceModels={serviceModels}
        />
      ))}
    </div>
  );
}

